"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import cx from "classnames";

import { useInterval } from "@anivive/hooks";
import { Email, Text } from "@anivive/ui";

import { HOST_NAME } from "@/constants";

import { checkUser } from "@/services/api/server/trials";

import Buttons from "../../components/buttons";
import { useWizard } from "../../context";
import styles from "../index.module.scss";

export default function StepOne() {
  const ref = useRef<HTMLFormElement | null>(null);
  const pathname = usePathname();

  const [error, setError] = useState({
    text: "",
    attempts: 0,
    backoffTimer: 0,
  });
  const [valid, setValid] = useState(false);

  const { actions, state } = useWizard();

  const { email, firstName, lastName } = state.data.user ?? {};

  const handleNext = async () => {
    if (email) {
      const { status } = await checkUser(email);

      if (status !== 200) {
        const { attempts } = error;

        if (attempts > 1) {
          const backoffTimer = (Math.pow(10, error.attempts) * 60) / 1000;

          return setError({
            text: "User already exists please log in.",
            attempts: attempts + 1,
            backoffTimer,
          });
        }

        return setError({
          text: "User already exists please log in.",
          attempts: attempts + 1,
          backoffTimer: 0,
        });
      }

      return actions.next();
    }
  };

  const renderError = () => {
    if (error.text) {
      const backoffTimerText =
        error.backoffTimer > 60
          ? `${Math.floor(error.backoffTimer / 60)}m ${error.backoffTimer % 60}s`
          : `${error.backoffTimer}s`;

      return (
        <p className={cx(styles.error, "text-style-body2-regular mb-4")}>
          {error.text} {error.backoffTimer ? `Try again in ${backoffTimerText}` : ""}
        </p>
      );
    }
  };

  useInterval(
    () =>
      setError((old) => ({
        ...old,
        backoffTimer: error.backoffTimer - 1,
      })),
    error.backoffTimer > 0 ? 1000 : null,
  );

  useEffect(() => setValid(ref.current?.checkValidity() ?? false), [state.data.user]);

  return (
    <div className={styles.container}>
      <form className="d-flex flex-column flex-md-row mb-4" ref={ref}>
        <div className={cx(styles.border, "col-md-6")}>
          <p className="text-style-body2-regular mb-4">
            If you already have an account, click the &quot;Log In&quot; button below.
          </p>

          <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1">
            <a href={`/auth/login?return_to=${HOST_NAME}${pathname}`} draggable={false}>
              <button type="button" className="primary text-style-body1-bold py-2 px-3">
                Log In
              </button>
            </a>
          </div>
        </div>

        <div className="d-flex flex-column col-md-6 ps-md-4">
          <p className="text-style-body2-regular mb-4">
            Create an account to monitor your pets&apos; progress through the application process.
          </p>

          {renderError()}

          <div className="d-flex flex-column flex-md-row mb-4">
            <label className="text-style-body1-regular mb-1 w-100">
              Email
              <Email
                className="w-100"
                onChange={(value) => {
                  actions.setData({
                    user: {
                      ...state.data.user,
                      email: value,
                    },
                    contact: {
                      ...state.data.contact,
                      email: value,
                    },
                  });

                  if (error.text && !error.backoffTimer) {
                    setError((old) => ({
                      ...old,
                      text: "",
                    }));
                  }
                }}
                required
                value={email}
              />
            </label>
          </div>

          <div className="d-flex flex-column flex-lg-row">
            <label className="text-style-body1-regular mb-4 mb-lg-0 w-100">
              First Name
              <Text
                className="w-100"
                onChange={(value) =>
                  actions.setData({
                    user: {
                      ...state.data.user,
                      firstName: value,
                    },
                  })
                }
                required
                value={firstName}
              />
            </label>

            <label className="text-style-body1-regular ms-lg-3 w-100">
              Last Name
              <Text
                className="w-100"
                onChange={(value) =>
                  actions.setData({
                    user: {
                      ...state.data.user,
                      lastName: value,
                    },
                  })
                }
                required
                value={lastName}
              />
            </label>
          </div>
        </div>
      </form>

      <Buttons disabled={!valid || !!(error.text || error.backoffTimer)} onNext={handleNext} />
    </div>
  );
}
